/** @jsx jsx */
import React, {
	useState,
	useMemo,
	useCallback,
	useRef,
	type FocusEvent,
	useEffect,
	forwardRef,
} from 'react';
import { cssMap, jsx, cx } from '@compiled/react';
import debounce from 'lodash/debounce';
import { v4 as uuid } from 'uuid';
import { graphql, useMutation } from 'react-relay';
import {
	components,
	type StylesConfig,
	type MultiValueGenericProps,
	type MultiValueRemoveProps,
	type OptionProps,
} from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import EditIcon from '@atlaskit/icon/core/edit';
import type { PopupComponentProps, TriggerProps } from '@atlaskit/popup';
import { Flex, Pressable } from '@atlaskit/primitives';
import { layers } from '@atlaskit/theme/constants';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { SessionId } from '@atlassian/jira-issue-analytics/src/common/types.tsx';
import { FailedFetchOptionWrapper } from '@atlassian/jira-issue-field-select-base/src/ui/failed-fetch-options/index.tsx';
import { filterOptionByLabelAndFilterValues } from '@atlassian/jira-issue-field-select-base/src/ui/filter-options-by-label-and-filter-values/index.tsx';
import { defaultSelectStyles } from '@atlassian/jira-issue-field-select-base/src/ui/react-select-styles/styled.tsx';
import type { LabelOption as Option } from '@atlassian/jira-issue-shared-types/src/common/types/labels-type.tsx';
import { LABELS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { LabelsSuggestionList } from '@atlassian/jira-shared-types/src/rest/jira/label.tsx';
import { LabelsColorPickerForm } from '@atlassian/jira-option-color-picker/src/labels-color-picker/index.tsx';
import type { ColorOptionData } from '@atlassian/jira-option-color-picker/src/labels-color-picker/types.tsx';
import { memoizeGetAutoGeneratedColorForLabels } from '@atlassian/jira-option-color-picker/src/option-color-generator/index.tsx';
import { OptionTag } from '@atlassian/jira-option-color-picker/src/option-tag/index.tsx';
import { getColorMetadata } from '@atlassian/jira-option-color-picker/src/color-picker/utils/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import type {
	edit_labelsUpdateColorMutation,
	JiraOptionColorInput,
} from '@atlassian/jira-relay/src/__generated__/edit_labelsUpdateColorMutation.graphql';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	fireTrackAnalyticsDeferred,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import { GREY_META } from '@atlassian/jira-option-color-picker/src/color-picker/constants.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { mapLabelsResponseToLabelOptions } from '../../common/utils.tsx';
import { useLabelsSuggestions } from '../../services/suggestions/index.tsx';
import CreatableSelectWithAnalytics from './creatable-select-with-analytics/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

export const FETCH_DEBOUNCE = 300;

const multiValueStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > div > div': {
			paddingLeft: token('space.0'),
			paddingRight: token('space.0'),
		},
	},
	isVisualRefreshDisabled: {
		borderRadius: token('border.radius.100'),
		backgroundColor: 'transparent',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div': {
			paddingLeft: token('space.050'),
			paddingRight: token('space.0'),
			paddingTop: token('space.0'),
			paddingBottom: token('space.0'),
		},
	},
});

const multiValueLabelStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > div': {
			borderColor: 'transparent',
		},
	},
});

const multiValueRemoveStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > span > svg': {
			transform: 'scale(1.5)',
			fill: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > span > svg > g > path': {
			d: 'path("m 2 10 l 4 -4 m 4 -4 L 6 6 m 0 0 L 2 2 m 4 4 l 4 4")',
			transform: 'translate(5px, 6px)',
			stroke: token('color.text'),
			strokeWidth: '1.5',
		},
	},
});

const labelsValueStyles: StylesConfig<Option, true> = {
	multiValue: (base, { data }) => {
		// @ts-expect-error - ts2322 - Type 'ReactNode' is not assignable to type 'string'.
		const dataLabel: string = data.label;

		const { backgroundColor: backgroundColorMilestone1 } = getColorMetadata(
			memoizeGetAutoGeneratedColorForLabels(dataLabel),
		);

		const isNewLabel = data.__isNew__;
		const newLabelColor = data.color ?? GREY_META.name;
		const oldLabelColor = data.color ?? memoizeGetAutoGeneratedColorForLabels(dataLabel);
		const { backgroundColor: backgroundColorMilestone2 } = getColorMetadata(
			isNewLabel ? newLabelColor : oldLabelColor,
		);

		const backgroundColor = expVal(
			'adding-color-to-labels-field-experiment-milestone2',
			'isColorfulNewLabelsEnabled',
			false,
		)
			? backgroundColorMilestone2
			: backgroundColorMilestone1;

		return {
			...base,
			border: `1px solid ${backgroundColor}`,
			'& > div > div > div': {
				paddingLeft: 0,
				paddingRight: 0,
			},
			...(!isVisualRefreshEnabled()
				? {
						borderRadius: token('border.radius.100'),
						backgroundColor: 'transparent',
						'& > div': {
							paddingLeft: token('space.050'),
							paddingRight: 0,
							paddingTop: 0,
							paddingBottom: 0,
						},
					}
				: {}),
		};
	},
	multiValueLabel: (base) => ({
		...base,
		'& > div > div': {
			borderColor: 'transparent',
		},
	}),
};

const labelsRemoveIconStyles: StylesConfig<Option, true> = {
	multiValueRemove: (base) => ({
		...base,
		'& > div > span > svg': {
			transform: 'scale(1.5)',
			fill: 'none',
		},
		'& > div > span > svg > g > path': {
			d: 'path("m 2 10 l 4 -4 m 4 -4 L 6 6 m 0 0 L 2 2 m 4 4 l 4 4")',
			transform: 'translate(5px, 6px)',
			stroke: token('color.text'),
			strokeWidth: '1.5',
		},
	}),
};

export const LabelsEdit = (props: Props) => {
	const {
		autoCompleteUrl,
		autoFocus,
		onChange,
		onFocus,
		onBlur,
		spacing,
		value,
		options,
		cachedOptions = [],
		formatCreateLabel,
		noOptionsMessage,
		loadingMessage,
		onCloseMenuOnScroll,
		isDropdownMenuFixedAndLayered,
		getSuggestions,
		fetchSuggestionsOnMount = true,
		fieldId = LABELS_TYPE,
		isInvalid = false,
		isDisabled = false,
		ariaLabel = '',
		inputId,
		isRequired,
		openMenuOnFocus,
	} = props;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [suggestions, setSuggestions] = useState<Option[]>();
	const [defaultSuggestions, setDefaultSuggestions] = useState<Option[]>();
	const [hasLastFetchFailed, setHasLastFetchFailed] = useState<boolean>(false);
	const lastQuery = useRef<string>('');
	const sessionId = useRef<SessionId>(uuid());

	const { data: project } = useProjectContext();

	const cloudId = useCloudId();

	const [commit] = useMutation<edit_labelsUpdateColorMutation>(graphql`
		mutation edit_labelsUpdateColorMutation($input: JiraBulkLabelColorUpdateInput!)
		@raw_response_type {
			jira {
				bulkUpdateLabelColor(input: $input) {
					success
					errors {
						message
					}
				}
			}
		}
	`);

	const isProjectAdmin = project?.isProjectAdmin || false;
	const isGlobalAdmin = useIsAdmin();

	const { formatMessage } = useIntl();
	const [{ isLoadingSuggestions }, { getLabelsSuggestions, setIsLoadingSuggestions }] =
		useLabelsSuggestions({ fieldId, getSuggestions });

	const getFailedFetchMessage = useCallback(
		() => (
			<FailedFetchOptionWrapper>{formatMessage(messages.failedFetch)}</FailedFetchOptionWrapper>
		),
		[formatMessage],
	);

	const fetchSuggestions = useCallback(
		async (query = ''): Promise<void> => {
			try {
				const response: LabelsSuggestionList = await getLabelsSuggestions(
					autoCompleteUrl,
					query,
					sessionId.current,
				);

				setIsLoadingSuggestions(false);

				if (lastQuery.current === query) {
					const results = mapLabelsResponseToLabelOptions(response);

					setSuggestions(results);

					if (query === '') {
						setDefaultSuggestions(results);
					}
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				setIsLoadingSuggestions(false);
				setHasLastFetchFailed(true);
				setSuggestions([
					{
						label: getFailedFetchMessage(),
						value: '',
						isDisabled: true,
					},
				]);
			}
		},
		[
			autoCompleteUrl,
			getFailedFetchMessage,
			getLabelsSuggestions,
			lastQuery,
			sessionId,
			setHasLastFetchFailed,
			setIsLoadingSuggestions,
		],
	);

	if (fg('_jira-concurrent-state-update-hasnt-mounted-yet')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			if (fetchSuggestionsOnMount) {
				if (!options && !defaultSuggestions) {
					setIsLoadingSuggestions(true);
					fetchSuggestions();
				} else if (options && !defaultSuggestions) {
					setDefaultSuggestions(options);
					setSuggestions(options);
				} else {
					lastQuery.current = '';
					setIsLoadingSuggestions(true);
					fetchSuggestions();
				}
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useMemo(() => {
			if (fetchSuggestionsOnMount) {
				if (!options && !defaultSuggestions) {
					setIsLoadingSuggestions(true);
					fetchSuggestions();
				} else if (options && !defaultSuggestions) {
					setDefaultSuggestions(options);
					setSuggestions(options);
				} else {
					lastQuery.current = '';
					setIsLoadingSuggestions(true);
					fetchSuggestions();
				}
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);
	}

	const groupedSuggestions = useMemo(
		() => [
			{
				label: formatMessage(messages.recent),
				options: cachedOptions,
			},
			{
				label: formatMessage(messages.all),
				options: suggestions || [],
			},
		],
		[cachedOptions, formatMessage, suggestions],
	);

	// go/jfe-eslint
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedFetchSuggestions = useCallback(debounce(fetchSuggestions, FETCH_DEBOUNCE), []);

	const allLabels = useCallback(
		() => [...(cachedOptions ?? []), ...(suggestions ?? [])],
		[cachedOptions, suggestions],
	);

	const isNewLabelText = useCallback(
		(inputValue?: string) => {
			const isNewLabel = !allLabels().some((option) => {
				// @ts-expect-error - ts2322 - Type 'ReactNode' is not assignable to type 'string'.
				const optionLabel: string = option.label;

				return optionLabel === inputValue;
			});
			return isNewLabel;
		},
		[allLabels],
	);

	const getCreateLabel = useCallback(
		(inputValue: string): string => {
			return `${inputValue} (${
				formatCreateLabel !== undefined ? formatCreateLabel : formatMessage(messages.createNewItem)
			})`;
		},
		[formatCreateLabel, formatMessage],
	);

	const getNoOptionsMessage = useCallback(
		(): string =>
			noOptionsMessage !== undefined ? noOptionsMessage : formatMessage(messages.empty),
		[formatMessage, noOptionsMessage],
	);
	const getLoadingMessage = useCallback(
		(): string => (loadingMessage !== undefined ? loadingMessage : formatMessage(messages.loading)),
		[formatMessage, loadingMessage],
	);

	const filterOption = useCallback(
		(option: Option, query: string): boolean => {
			if (hasLastFetchFailed) {
				// If the fetch has failed, we will show a disabled option to indicate the error to users
				return true;
			}
			return filterOptionByLabelAndFilterValues<Option>(option, query || '');
		},
		[hasLastFetchFailed],
	);

	const onQueryChange = useCallback(
		(query: string): void => {
			debouncedFetchSuggestions.cancel();
			setHasLastFetchFailed(false);
			if (query) {
				lastQuery.current = query;
				setIsLoadingSuggestions(true);
				debouncedFetchSuggestions(query);
			} else {
				lastQuery.current = '';
				setIsLoadingSuggestions(false);
				setSuggestions(defaultSuggestions);
			}
		},
		[debouncedFetchSuggestions, defaultSuggestions, setIsLoadingSuggestions, setSuggestions],
	);

	const [selectedLabelForEdit, setSelectedLabelForEdit] = useState<Option>();

	const [showColorPickerPopup, setShowColorPickerPopup] = useState(false);

	const [showColorPickerPopupWhenCreated, setShowColorPickerPopupWhenCreated] = useState(false);

	const onSelectFocus = useCallback(
		(e: FocusEvent<HTMLElement>): void => {
			if (!suggestions) {
				setIsLoadingSuggestions(true);
				fetchSuggestions('');
			}
			onFocus && onFocus(e);
		},
		[suggestions, onFocus, setIsLoadingSuggestions, fetchSuggestions],
	);

	const onSelectBlur = useCallback(
		(e: FocusEvent<HTMLElement>): void => {
			onBlur && onBlur(e);
		},
		[onBlur],
	);

	const handleOnChange = useCallback(
		(selectedLabels: Option[]) => {
			if (!selectedLabels) {
				return;
			}

			const selectedLabelsCount = selectedLabels.length;

			onChange?.(selectedLabels);

			// @ts-expect-error - TS2345 - Argument of type 'ReactNode' is not assignable to parameter of type 'string | undefined'.
			const isNewLabel = isNewLabelText(selectedLabels[selectedLabelsCount - 1].label);

			if (isNewLabel) {
				const newLabel = { ...selectedLabels[selectedLabelsCount - 1] };
				newLabel.__isNew__ = true;
				setSelectedLabelForEdit(selectedLabels[selectedLabelsCount - 1]);
				setShowColorPickerPopupWhenCreated(true);

				fireTrackAnalyticsDeferred(createAnalyticsEvent({}), 'newLabel created', {
					label: newLabel.label,
					fieldId,
				});
			}
		},
		[createAnalyticsEvent, fieldId, isNewLabelText, onChange],
	);

	const menuPosition = isDropdownMenuFixedAndLayered === true ? 'fixed' : undefined;

	const isLoading = isLoadingSuggestions;

	const onEditOption = (option: Option) => {
		setSelectedLabelForEdit(option);
		setShowColorPickerPopup(true);
	};

	const resetLabelsColorPickerState = () => {
		setSelectedLabelForEdit(undefined);
		setShowColorPickerPopup(false);
		setShowColorPickerPopupWhenCreated(false);
	};

	const submitLabelsUpdatedColor = (param: ColorOptionData) => {
		if (!selectedLabelForEdit) {
			return;
		}

		if (selectedLabelForEdit.__isNew__ && value) {
			const updatedColoredValue =
				value &&
				value.map((val) => {
					const isSelected = val.label === param.name;

					return {
						...val,
						...(isSelected ? { color: param?.color } : {}),
					};
				});
			onChange?.(updatedColoredValue);
		}

		const projectId = project?.projectId;

		let jiraProjectAri = null;
		try {
			jiraProjectAri =
				projectId &&
				cloudId &&
				expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				) &&
				JiraProjectAri.create({
					siteId: cloudId,
					projectId: projectId.toString(),
				}).toString();
		} catch (error) {
			fireErrorAnalytics({
				meta: {
					id: 'getProjectAriId',
					packageName: 'jiraIssueFieldLabels',
					teamName: 'gryffindor',
				},
				attributes: {
					projectId,
					cloudId,
				},
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				error: error as Error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}

		const analyticsEvent = createAnalyticsEvent({});

		if (jiraProjectAri) {
			commit({
				variables: {
					input: {
						projectId: jiraProjectAri,
						labelColorUpdate: [
							{
								fieldId,
								label: param.name,
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								color: param.color as JiraOptionColorInput,
							},
						],
					},
				},
				optimisticResponse: {
					jira: {
						bulkUpdateLabelColor: {
							success: true,
							errors: null,
						},
					},
				},
				onCompleted(mutationData) {
					if (mutationData?.jira?.bulkUpdateLabelColor?.success) {
						fetchSuggestions();

						const updatedSuggestions =
							suggestions &&
							suggestions.map((label) => {
								const isSelected = label.label === param.name;

								return {
									...label,
									...(isSelected ? { color: param.color } : {}),
								};
							});
						setSuggestions(updatedSuggestions);

						fireTrackAnalyticsDeferred(analyticsEvent, 'labelColor updated', { label: param });
					} else {
						const errorMessage =
							mutationData?.jira?.bulkUpdateLabelColor?.errors?.[0]?.message ?? undefined;
						fireErrorAnalytics({
							meta: {
								id: 'labelColorUpdate',
								packageName: 'jiraIssueFieldLabels',
								teamName: 'gryffindor',
							},
							attributes: {
								label: param,
								errorMessage,
							},
							sendToPrivacyUnsafeSplunk: true,
						});
					}
				},
				onError(error: Error) {
					fireErrorAnalytics({
						meta: {
							id: 'labelColorUpdate',
							packageName: 'jiraIssueFieldLabels',
							teamName: 'gryffindor',
						},
						attributes: {
							label: param,
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
				},
			});
		}
	};

	const popupContent = () => {
		const isNewLabel = selectedLabelForEdit?.__isNew__;
		const newLabelColor = selectedLabelForEdit?.color ?? '';
		const oldLabelColor =
			selectedLabelForEdit?.color ??
			memoizeGetAutoGeneratedColorForLabels(selectedLabelForEdit?.label?.toString());

		return (
			<LabelsColorPickerForm
				onCancel={() => {
					resetLabelsColorPickerState();
				}}
				onSubmit={(param: ColorOptionData) => {
					resetLabelsColorPickerState();
					submitLabelsUpdatedColor(param);
				}}
				initialData={{
					name: selectedLabelForEdit?.label?.toString() ?? '',
					color: isNewLabel ? newLabelColor : oldLabelColor,
				}}
			/>
		);
	};

	const styles = useMemo(() => {
		if (
			expVal('adding-color-to-labels-field-in-jira-experiment', 'isColoredLabelsEnabled', false) ||
			expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			)
		) {
			if (!isVisualRefreshEnabled()) {
				return { ...defaultSelectStyles, ...labelsValueStyles, ...labelsRemoveIconStyles };
			}
			return { ...defaultSelectStyles, ...labelsValueStyles };
		}

		return defaultSelectStyles;
	}, []);

	const styleComponents = useMemo(() => {
		if (
			expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			) ||
			expVal('adding-color-to-labels-field-in-jira-experiment', 'isColoredLabelsEnabled', false)
		) {
			return {
				MultiValueContainer: (multiValueProps: MultiValueGenericProps<Option>) => (
					<components.MultiValueContainer
						{...multiValueProps}
						xcss={cx(
							multiValueStyles.root,
							!isVisualRefreshEnabled() && multiValueStyles.isVisualRefreshDisabled,
						)}
					/>
				),
				MultiValueLabel: (multiValueLabelProps: MultiValueGenericProps<Option>) => (
					<components.MultiValueLabel {...multiValueLabelProps} xcss={multiValueLabelStyles.root} />
				),
				MultiValueRemove: (multiValueRemoveProps: MultiValueRemoveProps<Option>) => (
					<components.MultiValueRemove
						{...multiValueRemoveProps}
						xcss={!isVisualRefreshEnabled() && multiValueRemoveStyles.root}
					/>
				),
			};
		}
	}, []);

	const labelsField = (
		<CreatableSelectWithAnalytics
			isMulti
			autoFocus={autoFocus}
			fieldId={fieldId}
			options={groupedSuggestions}
			value={value}
			isDisabled={isDisabled}
			onBlur={onSelectBlur}
			menuPosition={menuPosition}
			styles={styles}
			onFocus={onSelectFocus}
			noOptionsMessage={getNoOptionsMessage}
			closeMenuOnScroll={onCloseMenuOnScroll}
			loadingMessage={getLoadingMessage}
			placeholder={formatMessage(messages.placeholder)}
			onInputChange={onQueryChange}
			onChange={
				expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				)
					? handleOnChange
					: onChange
			}
			spacing={spacing}
			isLoading={isLoading}
			filterOption={filterOption}
			{...(expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			)
				? {
						isValidNewOption: (inputValue: string) =>
							inputValue !== '' && isNewLabelText(inputValue),
					}
				: {})}
			formatCreateLabel={getCreateLabel}
			{...(!expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			) &&
			expVal('adding-color-to-labels-field-in-jira-experiment', 'isColoredLabelsEnabled', false)
				? {
						formatOptionLabel: (option: Option) =>
							RenderLabel(option, `(${formatMessage(messages.createNewItem)})`),
					}
				: {})}
			components={
				expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				)
					? {
							...styleComponents,
							Option: (optionProps: OptionProps<Option>) => {
								return (
									<components.Option {...optionProps}>
										{RenderLabel(
											optionProps.data,
											formatCreateLabel !== undefined
												? `(${formatCreateLabel})`
												: `(${formatMessage(messages.createNewItem)})`,
											onEditOption,
											isProjectAdmin,
											isGlobalAdmin,
										)}
									</components.Option>
								);
							},
						}
					: styleComponents
			}
			validationState={isInvalid === true ? 'error' : null}
			sessionId={sessionId.current}
			aria-label={ariaLabel}
			inputId={inputId}
			isRequired={isRequired}
			openMenuOnFocus={openMenuOnFocus}
			{...(expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			)
				? { UNSAFE_is_experimental_generic: true }
				: {})}
		/>
	);

	return (showColorPickerPopup || showColorPickerPopupWhenCreated) &&
		expVal(
			'adding-color-to-labels-field-experiment-milestone2',
			'isColorfulNewLabelsEnabled',
			false,
		) ? (
		<Popup
			zIndex={layers.modal()}
			isOpen={showColorPickerPopup || showColorPickerPopupWhenCreated}
			autoFocus={false}
			placement="bottom-start"
			fallbackPlacements={['bottom-end']}
			trigger={(triggerProps: TriggerProps) => (
				<span {...triggerProps} role="menu">
					{labelsField}
				</span>
			)}
			popupComponent={PopupContainer}
			onClose={resetLabelsColorPickerState}
			content={popupContent}
			messageId="issue-field-labels.ui.edit.popup.labels.popup"
			messageType="transactional"
		/>
	) : (
		labelsField
	);
};

const RenderLabel = (
	option: Option,
	createNewLabelString: string,
	onEditOption?: (option: Option) => void,
	isProjectAdmin?: boolean,
	isGlobalAdmin?: boolean,
) => {
	// @ts-expect-error - ts2322 - Type 'ReactNode' is not assignable to type 'string'.
	const optionLabel: string = option.label;

	if (
		expVal(
			'adding-color-to-labels-field-experiment-milestone2',
			'isColorfulNewLabelsEnabled',
			false,
		)
	) {
		if (typeof optionLabel === 'string' && optionLabel.endsWith(createNewLabelString)) {
			return (
				<Pressable backgroundColor="color.background.neutral.subtle">
					<OptionTag name={optionLabel} color={GREY_META.name} />
				</Pressable>
			);
		}

		return (
			<Flex justifyContent="space-between" role="group">
				<Pressable backgroundColor="color.background.neutral.subtle">
					<OptionTag
						name={optionLabel}
						color={option.color ?? memoizeGetAutoGeneratedColorForLabels(optionLabel)}
					/>
				</Pressable>
				{RenderInlineEditButton(option, onEditOption, isProjectAdmin, isGlobalAdmin)}
			</Flex>
		);
	}
	if (typeof optionLabel === 'string' && optionLabel.endsWith(createNewLabelString)) {
		const optionLabelWithoutNewLabel = optionLabel.slice(0, -createNewLabelString.length).trim();

		return (
			<OptionTag
				name={optionLabel}
				color={memoizeGetAutoGeneratedColorForLabels(optionLabelWithoutNewLabel)}
			/>
		);
	}

	return (
		<OptionTag name={optionLabel} color={memoizeGetAutoGeneratedColorForLabels(optionLabel)} />
	);
};

const RenderInlineEditButton = (
	option: Option,
	onEditOption?: (option: Option) => void,
	isProjectAdmin?: boolean,
	isGlobalAdmin?: boolean,
) => {
	if ((isProjectAdmin || isGlobalAdmin) && option.label)
		return (
			<IconButton
				testId={`labels-field-edit.inline-edit-button-${option.label}`}
				isDisabled={option.isDisabled}
				appearance="subtle"
				icon={EditIcon}
				spacing="compact"
				onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
					event.stopPropagation();
					onEditOption?.(option);
				}}
				label=""
			/>
		);

	return null;
};

type ExtendedPopupComponentProps = PopupComponentProps & {
	onClick?: (e: React.MouseEvent) => void;
};

const PopupContainer = forwardRef<HTMLDivElement, ExtendedPopupComponentProps>((props, ref) => (
	<div {...props} ref={ref} />
));

export default LabelsEdit;
